import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FolderPlus, LayoutGrid, ListChecks } from 'lucide-react';

import type { CreateProjectBody } from '@tasks/contracts';

import { createProject, listProjects, type ArchivedFilter } from '../../api/projects';
import { queryKeys } from '../../api/queryKeys';
import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { Page, PageHeader } from '../../components/Page';
import { Skeleton } from '../../components/Skeleton';
import { useToast } from '../../components/Toast';
import { humanizeError } from '../../lib/errors';
import { CreateProjectModal } from './CreateProjectModal';
import styles from './ProjectsPage.module.css';

const ARCHIVED_OPTIONS: readonly { readonly value: ArchivedFilter; readonly label: string }[] = [
  { value: 'false', label: 'Активные' },
  { value: 'true', label: 'Только архивные' },
  { value: 'all', label: 'Все' },
];

/** `/` — сетка карточек проектов (docs/06 §2, UC-PRJ-03). */
export function ProjectsPage() {
  const [archived, setArchived] = useState<ArchivedFilter>('false');
  const [isCreateOpen, setCreateOpen] = useState(false);
  const queryClient = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();

  const projectsQuery = useQuery({
    queryKey: queryKeys.projects.list(archived),
    queryFn: ({ signal }) => listProjects(archived, signal),
  });

  const createMutation = useMutation({
    mutationFn: (body: CreateProjectBody) => createProject(body),
    onSuccess: (project) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.projects.all });
      setCreateOpen(false);
      toast.success(`Проект ${project.key} создан`);
    },
    onError: (cause: unknown) => {
      toast.error(humanizeError(cause).message);
    },
  });

  return (
    <Page width="wide" gap="s-5">
      <PageHeader
        title="Мои проекты"
        actions={
          <div className={styles['headerActions']}>
            <div className={styles['segmented']} role="group" aria-label="Фильтр архивных проектов">
              {ARCHIVED_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  aria-pressed={archived === option.value}
                  className={archived === option.value ? styles['segmentActive'] : styles['segment']}
                  onClick={() => setArchived(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <Button
              variant="secondary"
              icon={<ListChecks size={16} />}
              onClick={() => navigate('/my')}
            >
              Моя доска
            </Button>
            <Button variant="primary" icon={<FolderPlus size={16} />} onClick={() => setCreateOpen(true)}>
              Создать проект
            </Button>
          </div>
        }
      />

      {projectsQuery.isPending ? (
        <div className={styles['grid']}>
          <Skeleton variant="card" count={6} />
        </div>
      ) : projectsQuery.isError ? (
        <EmptyState
          icon={<LayoutGrid size={20} />}
          title="Не удалось загрузить проекты"
          description={humanizeError(projectsQuery.error).message}
          action={
            <Button variant="primary" onClick={() => void projectsQuery.refetch()}>
              Повторить
            </Button>
          }
        />
      ) : projectsQuery.data.length === 0 ? (
        <EmptyState
          icon={<LayoutGrid size={20} />}
          title="Проектов пока нет"
          description="Создайте первый проект — статусы и переходы настроятся автоматически."
          action={
            <Button variant="primary" onClick={() => setCreateOpen(true)}>
              Создать проект
            </Button>
          }
        />
      ) : (
        <ul className={styles['grid']}>
          {projectsQuery.data.map((project) => (
            <li key={project.id}>
              <Link to={`/projects/${project.key}/board`} className={styles['card']}>
                <div className={styles['cardHead']}>
                  <span className={styles['key']}>{project.key}</span>
                  {project.isArchived ? <span className={styles['archived']}>В архиве</span> : null}
                </div>
                <p className={styles['name']}>{project.name}</p>
                {project.description ? <p className={styles['description']}>{project.description}</p> : null}
                <dl className={styles['counts']}>
                  <div>
                    <dt>Открыто</dt>
                    <dd>{project.counts.open}</dd>
                  </div>
                  <div>
                    <dt>Готово</dt>
                    <dd>{project.counts.done}</dd>
                  </div>
                  <div>
                    <dt>Участники</dt>
                    <dd>{project.counts.members}</dd>
                  </div>
                </dl>
              </Link>
            </li>
          ))}
        </ul>
      )}

      <CreateProjectModal
        open={isCreateOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={(body) => createMutation.mutate(body)}
        isPending={createMutation.isPending}
        error={createMutation.isError ? humanizeError(createMutation.error).message : null}
      />
    </Page>
  );
}
